import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAccountContext } from "@providers/AccountProvider";
import {
    bridgeReadinessProbeQueryKey,
    bridgeStatusQueryKey,
    bridgeSuitesQueryKey,
    columnActivityQueryKey,
    fetchBridgeStatus,
    fetchBridgeSuites,
    fetchColumnActivity,
    fetchSuitePreview,
    probeBridgeReadiness,
    suitePreviewQueryKey,
    type BridgeHealth,
    type BridgeStatus,
} from "@common/lib/api/bridge";
import {
    ACTIVITY_WINDOW_DAYS,
    STALL_AFTER_MS,
    type ConnectState,
    type NotWorkingKind,
} from "@/features/bridge-setup/bridge-setup-config";

/**
 * How long the install screen waits in silence before offering the three checks.
 *
 * Derived from `bridge-setup-config`'s `STALL_AFTER_MS` rather than redefined here — that
 * constant already drives the wizard's own stall timer, and two literals for one threshold
 * only invites them to drift apart.
 */
export const STALL_AFTER_MINUTES = STALL_AFTER_MS / 60_000;

/**
 * How long without a heartbeat counts as "went quiet". Three missed 5-minute heartbeats —
 * short enough to catch a real drop while the clinic is still watching, long enough that one
 * missed beat doesn't accuse a healthy bridge.
 */
export const LOST_CONTACT_AFTER_MINUTES = 15;

/** Poll cadence while the wizard is open. Deliberately not BridgeStatus.tsx's 5 minutes — someone watching a spinner needs a different cadence than a settings page. */
export const BRIDGE_POLL_MS = 5_000;

// `undefined` means "unknown elapsed time", not "infinite elapsed time" — those are different
// claims, and only the caller can decide what to do with "unknown" (a missing timestamp is not
// evidence of a lost connection, it's just a missing timestamp).
export const minutesSince = (iso: string | undefined): number | undefined =>
    iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 60_000) : undefined;

/**
 * Whether the bridge has positively told us it can reach BOTH the practice database and our
 * command channel.
 *
 * `=== true`, not truthiness, and that is the whole point: `null` means the bridge did not say,
 * and "did not say" is not evidence of readiness. Only two places construct a health report, and
 * exactly one of them omits these fields — the startup deactivation-gate POST
 * (happy-bridge/bridge/src/main.rs:320-330) sends `status: healthy` with both as `None`, before
 * any probe has run. The health loop proper always sends both booleans (`health.rs:91-92`
 * `Some(db_reachable)` / `Some(pubsub_reachable)`), so nulls are confined to that one startup row
 * and to legacy rows predating the fields.
 *
 * Exported for the same anti-drift reason `isFinishingSetup` is. `BridgeSetupFlow`'s entry gate has
 * to decide whether a clinic has genuinely finished, and it must reach that verdict with the same
 * predicate `deriveConnectState` uses below rather than with a second, looser reading of the bare
 * `status` string.
 *
 * `commandChannelProven` is the EN-969 readiness probe's verdict, and it substitutes for exactly
 * ONE of the two axes. An `fs.stat` round trip proves the command channel and says nothing
 * whatsoever about Cornerstone, so `db_reachable === true` is still required from a heartbeat.
 * One axis proven is not two, and the probe must not be able to promote a bridge that has never
 * said it can reach the practice database.
 */
export const bothChannelsConfirmed = (health: BridgeHealth, commandChannelProven = false): boolean =>
    health.db_reachable === true && (health.pubsub_reachable === true || commandChannelProven);

/**
 * Whether this health row is a bridge still finishing setup rather than one that is working or
 * one that is broken.
 *
 * Exported because two callers need the SAME answer and must not drift: `deriveConnectState`
 * below, and whoever owns the "when did setting-up begin" epoch that times the stall (see
 * `BridgeSetupFlow`). Deriving the epoch from a second, hand-copied version of this predicate is
 * how the timer comes to measure a different state than the one on screen.
 *
 * Assumes the clinic-actionable failures have already been ruled out — it does not re-check for
 * an explicitly unreachable database or staleness, because both outrank this and are handled
 * before it.
 *
 * Two ways in, and neither is a failure:
 *
 * 1. **Unconfirmed channels.** On a fresh install the Pub/Sub subscription does not exist yet.
 *    The bridge's consumer creates it (consumer.rs:279-306), and until it does, the connectivity
 *    watchdog's probe of that same subscription fails closed on NOT_FOUND - deliberately, and
 *    documented at gcp_credentials.rs:103-112 - and re-probes only every 30s (RECONNECT_POLL,
 *    main.rs:61). The health loop sends once at startup and then every 5 minutes
 *    (HEALTH_INTERVAL, health.rs:24). So the first heartbeat of a correct, healthy install
 *    reports `pubsub_reachable: false` - which the bridge maps to `degraded` (health.rs:69-74) -
 *    the channel genuinely comes up around t+30s, and no heartbeat carries that fact until
 *    t+5min. The startup-gate row before it reports healthy with both fields null. Rendering
 *    either as a failure told every healthy install "Something isn't working / And I can't tell
 *    you why yet" for five minutes, on a screen with no forward action at all.
 *
 *    This comment previously blamed task spawn ordering (health loop spawned at main.rs:410
 *    before the watchdog at :422). That was wrong and it misled an investigation. Reordering the
 *    spawns fixes nothing: the watchdog's first act is a network RPC, and on a fresh install it
 *    cannot succeed until the subscription exists, whatever order the tasks start in. The real
 *    fix for the wait is the EN-969 readiness probe (`commandChannelProven` below), which proves
 *    the channel with a live round trip instead of waiting for the next heartbeat to admit it.
 * 2. **Self-reported `degraded`** with both channels somehow confirmed. POST /bridge/health takes
 *    `status` as unenumerated free text, so this is defensive rather than something today's
 *    Cornerstone module emits.
 *
 * A bridge that cannot receive commands still must not read as `working` — it could never write a
 * document back. That requirement is intact; it is only the destination that changed, from a
 * dead-end verdict to a screen that keeps watching and self-heals.
 *
 * A self-reported `error` is deliberately NOT special-cased, and review has flagged that as a way
 * for a real failure to be masked as "still coming up". The trade is intentional and runs the other
 * way. `status` is unenumerated free text, and the screen this reaches is not a dead end: it keeps
 * watching, self-heals on the next heartbeat, and escalates to `settingUpStalled` — which offers a
 * route to us — once it persists. Trusting the word is what previously put every healthy install on
 * "Something isn't working", because the first heartbeat of a correct install reports an
 * unreachable command channel. Masking also needs the bridge to send `error` alongside channels it
 * has not confirmed, and today it maps an unreachable channel to `degraded` (health.rs:69-74), so
 * it does not emit that pairing. If it ever does, the fix is one `health.status !== "error"` term
 * here — but the regression to fear then is the false-failure screen, not this.
 *
 * Deliberately reads the HEARTBEAT's own confirmation only - `bothChannelsConfirmed(health)`, no
 * probe verdict threaded through. An earlier version took `commandChannelProven` as a second
 * parameter, and that let a successful probe DEMOTE the screen: a heartbeat reporting
 * `status: "error"` with `pubsub_reachable: false` starts here (unconfirmed channels, correctly
 * `settingUp`), and a landing probe flipped `bothChannelsConfirmed` to true while `statusOk` in
 * `deriveConnectState` stayed false - which skipped `working` (right) but ALSO skipped this branch,
 * falling through to the terminal catch-all's dead end, reached because a probe succeeded. That
 * regression is why the parameter was removed rather than special-cased.
 *
 * Promotion is unaffected: `deriveConnectState` evaluates the `working` branch - which does read
 * `commandChannelProven` via `bothChannelsConfirmed` and `statusOk` - before this one, so a probe
 * that actually proves the bridge ready still promotes there. This function only ever decides
 * between the self-healing screen and the terminal one, and a probe contradicting a heartbeat's
 * `pubsub_reachable: false` is not evidence of failure - it is the heartbeat not having caught up
 * yet, which is the definition of still coming up.
 */
export const isFinishingSetup = (health: BridgeHealth): boolean =>
    !bothChannelsConfirmed(health) || health.status === "degraded";

/**
 * Maps installation + health to the install screen's user-facing state.
 *
 * Pure and exported so the mapping is testable without a query client — this is the part
 * that decides whether a clinic sees "you're connected" or gets sent to support.
 *
 * `minutesWaiting` and `minutesSettingUp` are two different clocks on purpose. The first runs
 * from the download and times "we have never heard from it"; the second runs from the first
 * finishing-setup heartbeat and times "it's connected but not ready". Sharing one clock made the
 * setup stall assert "this is taking longer than it usually does" in the first second of setting
 * up: a bridge that took nine minutes to connect arrived with nine of its ten-minute budget
 * already spent, and since heartbeats are five minutes apart, the very first report could land
 * past the threshold. Same threshold value, different epoch.
 *
 * Known v1 limitation (wiring spec §9): `working` means the bridge *reports* healthy AND
 * reports both channels reachable, not that it answered a real query. A bridge that reaches us,
 * reaches Cornerstone, and reaches the command channel but still can't complete a round trip
 * reads as working here — that gap closes when the deferred verification probe lands.
 */
export const deriveConnectState = (
    status: BridgeStatus | undefined,
    minutesWaiting: number,
    statusUnavailable = false,
    minutesSettingUp = 0,
    commandChannelProven = false,
): { state: ConnectState; notWorkingKind: NotWorkingKind; minutesSinceContact: number } => {
    const quietFor = minutesSince(status?.last_seen_at);
    // `minutesSinceContact` is a UI-facing number — it must never surface as `Infinity`/`NaN`.
    // 0 stands in for "unknown", same as every branch below that has no meaningful elapsed time.
    const minutesSinceContact = quietFor ?? 0;
    const fallback = { notWorkingKind: "unknown" as NotWorkingKind, minutesSinceContact: 0 };

    // Checked first, and only when there's no status at all to reason from: our endpoint failing
    // is not evidence about the clinic's install. Without this the `!status?.installed` branch
    // below turns our own 500 into "did the installer finish? did it run as administrator?",
    // which can talk a clinic into uninstalling a bridge that works.
    //
    // A failing poll that follows a SUCCESSFUL one deliberately keeps the last known state
    // rather than flipping to `cantCheck`: that status is real evidence, only possibly stale,
    // and flapping between a verdict and "we can't tell" on a 5s poll is worse than either.
    if (statusUnavailable && !status) {
        return { ...fallback, state: "cantCheck" };
    }

    if (!status?.installed) {
        return {
            ...fallback,
            state: minutesWaiting >= STALL_AFTER_MINUTES ? "stalled" : "waiting",
        };
    }

    // Installed but nothing reported yet: the installer ran and registration landed, and the
    // first heartbeat hasn't arrived. Still "waiting" — never "stalled", because we have
    // positive evidence something is happening.
    if (!status.latest_health) return { ...fallback, state: "waiting" };

    const health = status.latest_health;

    // db_reachable is checked before staleness because it's the one with an instruction
    // attached ("check the PIMS server is on"). A stale heartbeat is a symptom; an
    // unreachable database is a thing the clinic can act on.
    //
    // `=== false`, not `!db_reachable`. The bridge's startup deactivation-gate POST
    // (happy-bridge/bridge/src/main.rs:320-330) sends `status: healthy` with `db_reachable: None`,
    // which the server stores as null — so on a fresh, correct install the FIRST row on record has
    // no verdict about Cornerstone at all. Read loosely, that row rendered "The Bridge can't reach
    // Cornerstone / Check that Cornerstone is running" to a clinic whose Cornerstone was fine.
    // "Didn't say" is not "said no".
    if (health.db_reachable === false) {
        return { state: "notWorking", notWorkingKind: "cantReachPims", minutesSinceContact };
    }

    // A missing `last_seen_at` alongside a reported health check is unknown elapsed time, not
    // lost contact — the health report itself is recent evidence of life, so `quietFor` being
    // undefined must never force a `lostContact` verdict (only an actually-stale timestamp can).
    if (quietFor !== undefined && quietFor >= LOST_CONTACT_AFTER_MINUTES) {
        return { state: "notWorking", notWorkingKind: "lostContact", minutesSinceContact };
    }

    // An admin-deactivated installation looks healthy from the bridge's side but will never
    // drive appointments. Surfacing it as "working" would strand the clinic with no signal.
    if (status.is_active === false) {
        return { state: "notWorking", notWorkingKind: "unknown", minutesSinceContact };
    }

    // POSITIVE evidence, on all three axes. The reported word alone is not enough: POST
    // /bridge/health takes `status` as bridge-self-reported free text with no server-side
    // enumeration, so a bridge can call itself `healthy` while saying nothing at all about whether
    // it can reach Cornerstone or our command channel — which is exactly what the startup-gate row
    // does (main.rs:320-330, both fields `None`).
    //
    // This is what the contract above has always claimed and what the code did not enforce. The
    // cost of the gap was not cosmetic: `working` is what fires the staff/room import, and
    // `importStartedRef` latches so it never retries. Reading a null row as working meant
    // sync-providers ran against a bridge of unproven readiness, once, with no second chance —
    // a live route into the empty-staff-and-rooms state, reached on a healthy install.
    const channelsConfirmed = bothChannelsConfirmed(health, commandChannelProven);

    // `degraded` is not taken at face value when the probe has landed, and that is not a
    // loosening of the contract above - it is reading the word correctly. The probe promotes a
    // `degraded` heartbeat only when it supplies the axis the heartbeat did not confirm:
    // `pubsub_reachable !== true` is the heartbeat leaving the command channel unconfirmed, and
    // the probe's live round trip is newer and stronger evidence about that same axis than the
    // heartbeat's own <=30s-stale watchdog read. A heartbeat that already reported BOTH channels
    // reachable and still called itself degraded has given no account of why - the probe cannot
    // supply an explanation it was never asked for, and that is exactly the defensive case
    // `isFinishingSetup`'s doc above names as one that must stay on the self-healing screen.
    const statusOk =
        health.status === "healthy" ||
        (health.status === "degraded" &&
            commandChannelProven &&
            health.pubsub_reachable !== true &&
            health.db_reachable === true);

    if (statusOk && channelsConfirmed) {
        return { ...fallback, state: "working", minutesSinceContact };
    }

    // Ranked below the two clinic-actionable verdicts, which must keep winning when both are true:
    // there is nothing on that computer to do about a bridge that is merely still coming up, while
    // `cantReachPims` says "check the PIMS server is on" and `lostContact` says "restart
    // HappyBridge near the clock".
    //
    // Above the terminal catch-all because a bridge that hasn't confirmed its channels is not a
    // bridge that has failed — it is the normal state of a correct install for its first few
    // seconds, and of the first heartbeat for its first five minutes (see `isFinishingSetup`). So
    // it gets the screen written for exactly this, and escalates only once it has PERSISTED,
    // measured from when setting up began rather than from the download. Before that timer existed
    // this state spun forever; before the separate epoch, it accused a bridge of being slow in its
    // first second.
    if (isFinishingSetup(health)) {
        return {
            ...fallback,
            state: minutesSettingUp >= STALL_AFTER_MINUTES ? "settingUpStalled" : "settingUp",
            minutesSinceContact,
        };
    }

    // The terminal catch-all, and now narrow by construction: everything we can check is positively
    // fine — both channels confirmed, contact fresh, activation on — and the bridge still calls
    // itself something other than healthy. That genuinely is unexplained, which is what this kind's
    // copy says.
    return { state: "notWorking", notWorkingKind: "unknown", minutesSinceContact };
};

export const useBridgeStatus = ({ enabled = true, pollMs = BRIDGE_POLL_MS } = {}) => {
    const { accountData } = useAccountContext();
    return useQuery({
        queryKey: bridgeStatusQueryKey(accountData?.id),
        queryFn: fetchBridgeStatus,
        enabled,
        refetchInterval: pollMs,
        // Always refetch: a cached "not installed" is exactly the answer that must not
        // survive the install completing.
        staleTime: 0,
    });
};

/** Probe cadence while the install screen is open and the channel is still unproven. */
export const PROBE_POLL_MS = 10_000;

/**
 * Whether a live round trip to the clinic's bridge has completed during this session (EN-969).
 *
 * Returns a LATCHED boolean, and the latch is the design rather than an optimisation. "A probe
 * never demotes a bridge" has to be a property of the code, not a rule each caller remembers -
 * so once a probe lands, the query is disabled, no further round trips are made, and there is no
 * path back to `false` short of unmounting.
 *
 * Every non-success is silent and means "no information", never "not ready". A miss is the
 * EXPECTED result for the first ~30s of a correct install: GCP Pub/Sub only delivers messages
 * published after a subscription exists, and the bridge's consumer has not created it yet
 * (consumer.rs:279-306), so those commands are dropped and can never be answered. Reading a miss
 * as evidence would be reading the absence of a subscription as the absence of a bridge.
 *
 * `retry: false` for the same reason the cadence is fixed: the poll IS the retry, and React
 * Query's own backoff on top of it would only make the interval unpredictable.
 */
export const useBridgeReadinessProbe = ({ enabled = true } = {}) => {
    const { accountData } = useAccountContext();
    const [proven, setProven] = useState(false);

    const { data } = useQuery({
        queryKey: bridgeReadinessProbeQueryKey(accountData?.id),
        queryFn: probeBridgeReadiness,
        enabled: enabled && !proven,
        refetchInterval: PROBE_POLL_MS,
        retry: false,
        staleTime: 0,
        // Not cached past the wizard: a probe result is a claim about a moment, and a stale
        // cached `true` promoting a later mount is exactly what this must never do.
        gcTime: 0,
    });

    useEffect(() => {
        if (data?.ready === true) setProven(true);
    }, [data?.ready]);

    return proven;
};

/**
 * Suites, rooms, and the activity-based recommendation for the Cornerstone "appointment suite"
 * step.
 *
 * `suiteId` is keyed into the query (see `bridgeSuitesQueryKey`) so switching the dropdown
 * refetches rather than reusing a coverage warning computed for the suite the clinic just left —
 * the server derives `rooms` / `in_selected_suite` for exactly one suite per response.
 *
 * `staleTime: 0`, unlike `useColumnActivity`'s five minutes: this screen's whole job is telling
 * the clinic whether the rooms in front of them are the right ones, and a `suiteId` change without
 * a live refetch would show a warning that describes a suite they didn't choose.
 */
export const useBridgeSuites = (suiteId: number | null, { enabled = true } = {}) => {
    const { accountData } = useAccountContext();
    return useQuery({
        queryKey: bridgeSuitesQueryKey(accountData?.id, suiteId),
        queryFn: () => fetchBridgeSuites(suiteId),
        enabled,
        staleTime: 0,
    });
};

/**
 * Today's (or the fallback day's) appointments for the EN-1199 preview grid on the suite step.
 *
 * `staleTime: 5 * 60_000`, unlike `useBridgeSuites`'s zero: unlike the suite/room read, the day's
 * appointments don't depend on which suite is selected, so nothing about a dropdown change should
 * force a refetch of the same day's board, and this mirrors `useColumnActivity`'s reasoning for
 * a window that doesn't change while someone walks a few screens.
 */
export const useSuitePreview = ({ enabled = true } = {}) => {
    const { accountData } = useAccountContext();
    return useQuery({
        queryKey: suitePreviewQueryKey(accountData?.id),
        queryFn: fetchSuitePreview,
        enabled,
        staleTime: 5 * 60_000,
    });
};

export const useColumnActivity = ({ enabled = true } = {}) => {
    const { accountData } = useAccountContext();
    return useQuery({
        // The window is part of the identity of the result, not just an argument to it — hence
        // the shared builder rather than a literal here. See its own doc for why: this was a bare
        // key literal in `BridgeSetupFlow`'s invalidation and this hook's own key independently,
        // which is exactly the drift `bridgeStatusQueryKey` / `bridgeSuitesQueryKey` already exist
        // to prevent for their own endpoints.
        queryKey: columnActivityQueryKey(accountData?.id),
        // Passed explicitly rather than leaning on fetchColumnActivity's own default. Three
        // wizard screens print this number in their copy ("appointments in the last 60 days"),
        // and a default on the other side of the API module is exactly how that copy comes to
        // describe a window the query didn't ask for — the same drift STALL_AFTER_MINUTES is
        // derived to avoid.
        queryFn: () => fetchColumnActivity(ACTIVITY_WINDOW_DAYS),
        enabled,
        // The window does not change while someone walks three screens.
        staleTime: 5 * 60_000,
    });
};
